<template>
  <div id="fileupload">
    <form
      id="attachment-desc-form"
      v-if="overallDescription"
    >
      <textarea
        type="text"
        class="autosize "
        v-autosize
        v-hkldinput
        v-model="attachment.description"
      />
      <div class="fileupload-desc-hint">
        <popover
          :text="$t('tender.attachment.descriptionPlaceholder')"
          position="bottom-left"
        />
      </div>
    </form>
    <HkldDropzone
      :sourceId="resourceId || ''"
      v-if="!readonly"
      ref="hkldDropzone"
      :metaData="metaData"
      :uploadDocTypes="uploadDocTypes"
      :maxNumberOfFiles="maxNumberOfFiles"
      :maxTotalFileSizeInBytes="maxTotalFileSizeInBytes"
      :existingFiles="attachment.files"
      :uploadNoteRows="uploadNoteRows"
      :filesExceedMessage="filesExceedMessage"
      :totalSizeExceedMessage="totalSizeExceedMessage"
      :useTotalSizeMessageForFileSizeError="useTotalSizeMessageForFileSizeError"
    />

    <p
      class="hint"
      v-if="showTenderHints"
    >{{ $t('tender.attachment.attachmentNotes') }}</p>
    <table
      class="tender-tnc-table"
      v-show="attachment.files.length>0"
    >
      <thead>
        <th class="number tender-font">#</th>
        <th class="filename tender-font">{{ $t('tender.attachment.document') }}</th>
        <th class="description tender-font">{{ $t('tender.attachment.description') }}</th>
        <th />
      </thead>
      <draggable
        :options="{handle:'.myHandler'}"
        element="tbody"
        v-model="files"
        @end="onDraggableEnd"
      >
        <template v-for="(item, index) in files">
          <tr
            class="set-border-top"
            :key="index"
          >
            <td class="number tender-font myHandler">{{ index + 1 }}</td>
            <td class="filename tender-font myHandler">
              <a
                style="text-decoration: underline;"
                @click="downloadFile(item.id)"
              >{{ item.filename }}</a>
            </td>
            <td class="description tender-font">
              <textarea
                v-file-description-value="item.description"
                v-autosize="{defer: true}"
                class="file-upload-desc autosize"
                :class="{'error-highlight': invalidDescriptionLength && (item.description.length > maxDescriptionLength)}"
                :disabled="readonly"
                :maxlength="maxDescriptionLength"
                @input="event => updateDescriptionDraft(item, event.target.value)"
                @change="event => commitDescription(item, event.target.value)"
              />
            </td>
            <td>
              <div
                class="file-upload-delete"
                @click="deleteFile(index, item)"
                v-if="!readonly"
              >&#x2715;</div>
            </td>
          </tr>
        </template>
      </draggable>
    </table>

  </div>
</template>

<script>
import HkldDropzone from '@/controls/HkldDropzone'
import Popover from '@/controls/Popover'
import HTTP from '@/utils/http'
import * as xfs from '@/utils/xfs'
import EventBus from '@/utils/eventBus'
import draggable from 'vuedraggable'
import { maxAttachmentDescriptionLength } from '@/utils/consts'
import lodash from 'lodash'

export default {
  name: 'file-upload',
  components: {
    HkldDropzone,
    Popover,
    draggable,
  },
  directives: {
    fileDescriptionValue: {
      inserted (el, binding) {
        el.value = binding.value || ''
      },
      componentUpdated (el, binding) {
        if (document.activeElement === el) return
        const value = binding.value || ''
        if (el.value !== value) {
          el.value = value
        }
      },
    },
  },

  data () {
    return {
      description: '',
      maxDescriptionLength: maxAttachmentDescriptionLength,
      prevSourceId: '',
      files: [],
      lineActions: [
        { name: 'Delete', action: 'deleteFile' },
        { name: 'Download', action: 'downloadFile' },
      ],
    }
  },

  props: {
    overallDescription: {
      type: Boolean,
      default: false,
    },
    attachment: {
      type: Object,
    },
    resourceId: {
      type: String,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    invalidDescriptionLength: {
      type: Boolean,
      default: false,
    },
    showTenderHints: {
      type: Boolean,
      default: false,
    },
    initLoading: {
      type: Boolean,
      default: true,
    },
    metaData: {
      type: Object,
      required: false,
    },
    minFiles: {
      type: Number,
      required: false,
    },
    uploadDocTypes: {
      type: String,
      default: '',
    },
    maxNumberOfFiles: {
      type: Number,
      default: 100,
    },
    maxTotalFileSizeInBytes: {
      type: Number,
      default: 0,
    },
    uploadNoteRows: {
      type: Array,
      default: () => [],
    },
    filesExceedMessage: {
      type: String,
      default: '',
    },
    totalSizeExceedMessage: {
      type: String,
      default: '',
    },
    useTotalSizeMessageForFileSizeError: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    validate () {
      this.abide.validateForm()

      // * Be careful, the validate was designed to return *invalid* status at the first time mistakenly
      return this.$el.querySelectorAll('textarea.is-invalid-input').length !== 0
    },

    validateMinFiles () {
      const valid = this.minFiles ? this.files.length >= this.minFiles : true

      const form = this.$refs.hkldDropzone.$el.querySelector('form.dropzone')
      if (form) form.classList[valid ? 'remove' : 'add']('is-invalid-input')

      return valid
    },

    updateOrders () {
      this.attachment.files.map((file, index) => {
        file.order = index
      })
    },

    async deleteFile (index, item) {
      await HTTP.delete(`/docs/${item.id}`)
      this.attachment.files.splice(index, 1)
      this.$refs.hkldDropzone.removeFile(item)
      this.updateOrders()
    },

    async downloadFile (fileId) {
      await xfs.downloadFile(`/docs/${fileId}`)
    },

    updateDescriptionDraft (item, value) {
      if (Object.hasOwnProperty.call(item, '__descriptionDraft')) {
        item.__descriptionDraft = value
      } else {
        Object.defineProperty(item, '__descriptionDraft', {
          configurable: true,
          writable: true,
          value,
        })
      }
    },

    commitDescription (item, value) {
      this.updateDescriptionDraft(item, value)
      item.description = value
    },

    filterByCategory (files) {
      const useFilter = files && files.length && this.useCategory
      return useFilter ? files.filter(
        ({metaData}) => lodash.get(metaData, 'category') === this.metaData.category,
      ) : files
    },

    async loadFiles () {
      if (this.resourceId) {
        const response = await HTTP.get(`/docs/?src=${this.resourceId}`)
        this.attachment.files = this.filterByCategory(response.data.data)
      }
    },

    addNewItem (newItem) {
      if (this.useCategory) {
        const newItemCategory = lodash.get(newItem.metaData, 'category')
        if (newItemCategory !== this.metaData.category) return
      }

      let isExist = false
      this.attachment.files.map((item) => {
        if (item.id === newItem.id) {
          isExist = true
        }
        return isExist
      })
      if (!isExist) {
        newItem.order = this.attachment.files.length
        this.attachment.files.push(newItem)
      }
    },
    setupListener () {
      EventBus.$off(`controls.hkldDropzone.newItem.${this.prevSourceId}`)
      EventBus.$on(`controls.hkldDropzone.newItem.${this.resourceId}`, (newItem) => {
        this.addNewItem(newItem)
      })
      this.prevSourceId = this.resourceId
    },
    onDraggableEnd (event) {
      this.attachment.files = this.files
      this.updateOrders()
    },
  },

  async mounted () {
    if (this.initLoading) {
      await this.loadFiles()
    }
    this.setupListener()
    this.form = $('#attachment-desc-form')
    this.abide = new Foundation.Abide(this.form, {
      liveValidate: true,
    })
    this.files = lodash.get(this.attachment, 'files', [])
  },

  computed: {
    useCategory () {
      return this.metaData &&
        Object.hasOwnProperty.call(this.metaData, 'category')
    },
  },

  watch: {
    async resourceId () {
      this.setupListener()
      if (this.initLoading) {
        await this.loadFiles()
      }
    },
    // Shallow: avoid re-running on every in-place edit (e.g. description keystrokes) — those were tanking INP.
    'attachment.files': function (newFiles) {
      this.files = newFiles || []
    },
  },
}
</script>

<style scoped lang="scss">
@import '~styles/tender/attachments';

#fileupload {
  border: none !important;
}
#attachment-desc-form {
  position: relative;
  margin-bottom: 20px;
}

.fileupload-desc-hint {
  color: #6e7a91;
  position: absolute;
  top: 0;
  right: -20px;
}
.error-highlight {
  background-color: #f9ecea!important;
}
.myHandler:hover {
  cursor: move;
}
</style>
